/**
 * Shared package actions + relation builders for the Package-Graph entity group
 * (packages, skills/contours facades, bets/frames/principles facades).
 *
 * The named primary actions from the acceptance matrix (frontend-entity-coverage
 * §2.2) live here so every typed package show renders them identically:
 *
 *   - **compile**          → `POST /api/packages/:id/compile`  `[PROPOSED]`
 *   - **advance maturity** → `PATCH maturity_stage` (10-stage ladder, `03 §2`)
 *   - **request review**   → `PATCH status='review'` (Bet governance gate)
 *   - **run / dispatch**   → reuses the AgentRun dispatch path `[PROPOSED]`
 *
 * `[PROPOSED]` endpoints degrade gracefully: the action surfaces a calm error
 * toast (via `useApiAction`) and never hard-crashes the show page. Maturity
 * advancement rides the existing Refine `update` (boxed dataProvider → PATCH
 * `/api/packages/:id`), so it works on the live system today.
 *
 * `buildPackageRelations` produces the Inspector `Relations` groups (outgoing +
 * incoming `package_links`) shared by every package show, each endpoint a
 * `RefLink` to the `packages` resource.
 */
import * as React from "react";
import { useUpdate } from "@refinedev/core";
import {
  ArrowUpRight,
  ArrowDownLeft,
  Boxes,
  TrendingUp,
} from "@/components/ui/icon";

import {
  PACKAGE_MATURITY_STAGES,
  type Package,
  type PackageLink,
  type PackageMaturityStage,
} from "@aos/shared";
import { Button } from "@/components/ui/button";
import { ConfirmDialog } from "@/components/app/ConfirmDialog";
import { useApiAction } from "@/resources/_shared/useApiAction";
import { humanize } from "@/resources/projects/_form";
import {
  type RelationGroup,
  type InspectorAction,
} from "@/components/inspector";

/** The next maturity stage on the ladder, or `null` at the terminal stage. */
export function nextMaturityStage(
  stage: PackageMaturityStage,
): PackageMaturityStage | null {
  // `deprecated` is out of the forward ladder — it has no "advance" target.
  if (stage === "deprecated") return null;
  const order = PACKAGE_MATURITY_STAGES.filter((s) => s !== "deprecated");
  const idx = order.indexOf(stage);
  if (idx < 0 || idx >= order.length - 1) return null;
  return order[idx + 1] ?? null;
}

/**
 * `compile` primary action (`[PROPOSED]` `POST /api/packages/:id/compile`).
 * Renders as a ghost button; degrades to a calm toast when the endpoint is
 * absent (the `useApiAction` error path), never crashing the page.
 */
export function CompileAction({ packageId }: { packageId: string }) {
  const { run, loading } = useApiAction();
  return (
    <Button
      variant="outline"
      size="sm"
      disabled={loading}
      data-testid="package-compile"
      onClick={() =>
        void run(`/packages/${encodeURIComponent(packageId)}/compile`, {
          method: "POST",
          invalidateResources: ["packages"],
          successMessage: "Compilation requested.",
        }).catch(() => {
          /* [PROPOSED] endpoint: error surfaced as a toast, page stays alive. */
        })
      }
    >
      <Boxes className="mr-2 h-4 w-4" />
      {loading ? "Compiling…" : "Compile"}
    </Button>
  );
}

/**
 * `advance maturity` action — PATCHes `maturity_stage` to the next ladder stage
 * via the boxed Refine dataProvider. Renders nothing at the terminal stage.
 */
export function AdvanceMaturityAction({
  pkg,
  onDone,
}: {
  pkg: Package;
  onDone?: () => void;
}) {
  const next = nextMaturityStage(pkg.maturity_stage);
  const { mutate, isLoading } = useUpdate();
  const [open, setOpen] = React.useState(false);

  if (!next) return null;

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        disabled={isLoading}
        data-testid="package-advance-maturity"
        onClick={() => setOpen(true)}
      >
        <TrendingUp className="mr-2 h-4 w-4" />
        Advance maturity
      </Button>
      <ConfirmDialog
        open={open}
        onOpenChange={setOpen}
        title="Advance maturity stage?"
        description={
          <>
            Move <span className="font-medium">{pkg.name}</span> from{" "}
            <span className="font-medium">{humanize(pkg.maturity_stage)}</span>{" "}
            to <span className="font-medium">{humanize(next)}</span>.
          </>
        }
        confirmLabel="Advance"
        confirmVariant="default"
        loading={isLoading}
        onConfirm={() =>
          new Promise<void>((resolve, reject) => {
            mutate(
              {
                resource: "packages",
                id: pkg.id,
                values: { maturity_stage: next },
              },
              {
                onSuccess: () => {
                  onDone?.();
                  resolve();
                },
                onError: (err) => reject(err),
              },
            );
          })
        }
      />
    </>
  );
}

/**
 * Build the Inspector `Relations` groups for a package: outgoing links (this
 * package as the source) and incoming links (this package as the target). Each
 * endpoint resolves to a readable label via `RefLink` against the `packages`
 * resource. Empty groups still render their heading + a calm note.
 */
export function buildPackageRelations(
  pkg: Package,
  outgoing: PackageLink[],
  incoming: PackageLink[],
): RelationGroup[] {
  return [
    {
      title: "Outgoing links",
      emptyMessage: "No outgoing package links.",
      items: outgoing.map((link) => ({
        key: link.id,
        label: humanize(link.link_type),
        icon: ArrowUpRight,
        resource: "packages",
        id: link.target_package_id,
        labelField: "name",
      })),
    },
    {
      title: "Incoming links",
      emptyMessage: "No incoming package links.",
      items: incoming.map((link) => ({
        key: link.id,
        label: humanize(link.link_type),
        icon: ArrowDownLeft,
        resource: "packages",
        id: link.source_package_id,
        labelField: "name",
      })),
    },
  ];
}

/**
 * Shared overflow (`…` menu) actions for a package show: Edit + Delete, routed
 * through the supplied navigation/delete callbacks. The caller owns the
 * Edit/Delete affordances so each facade can route to its own resource path.
 */
export function packageMenuActions({
  onEdit,
  onDelete,
  editIcon,
  deleteIcon,
}: {
  onEdit?: () => void;
  onDelete?: () => void;
  editIcon: React.ComponentType<{ className?: string }>;
  deleteIcon: React.ComponentType<{ className?: string }>;
}): InspectorAction[] {
  const actions: InspectorAction[] = [];
  if (onEdit) {
    actions.push({ label: "Edit", onClick: onEdit, icon: editIcon });
  }
  if (onDelete) {
    actions.push({
      label: "Delete",
      onClick: onDelete,
      icon: deleteIcon,
      destructive: true,
    });
  }
  return actions;
}
